/**
 * VersionRefreshToast
 *
 * After a deploy, tabs that were already open keep running the stale
 * JS bundle. This mirrors the Brij Signal "new version available" mechanism:
 * the bundle bakes in its own commit (__APP_COMMIT__) at build time and
 * a small static version.json is emitted alongside it. We poll that
 * file on an interval (visible tabs only) and whenever the tab regains
 * focus / becomes visible; when the served commit no longer matches the
 * one baked into the running bundle, a newer build is live.
 *
 * On mismatch we surface a NON-BLOCKING, dismissible toast with a
 * Refresh button. We never auto-reload — a reload would discard an
 * in-progress draft. The user chooses when to reload.
 *
 * Brand-aware + theme-aware (styled purely with design-system CSS
 * tokens), RTL-safe (logical CSS properties, no left/right), and
 * i18n via useLanguage (en + he).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'antd';
import { ReloadOutlined, CloseOutlined } from '@ant-design/icons';
import { useLanguage } from '@/hooks/useLanguage';

const VERSION_URL = '/version.json';
const POLL_INTERVAL_MS = 5 * 60 * 1000; // ~5 minutes
// Floor between checks so a burst of focus/visibility events (e.g. rapid
// alt-tabbing) can't fire a network round-trip more than once per window.
const MIN_CHECK_INTERVAL_MS = 30 * 1000; // 30 seconds

// The commit baked into this bundle at build time. Guarded so the app
// never crashes if the define is somehow absent.
const BAKED_COMMIT =
  typeof __APP_COMMIT__ !== 'undefined' ? __APP_COMMIT__ : 'dev';

/**
 * Fetch the currently-deployed commit from the static version.json.
 * Returns null on any failure (e.g. local dev where the file is absent,
 * or a transient network error) so the caller stays silent.
 */
async function fetchDeployedCommit(): Promise<string | null> {
  try {
    const res = await fetch(VERSION_URL, { cache: 'no-store' });
    if (!res.ok) return null;
    const data: unknown = await res.json();
    const commit = (data as { commit?: unknown })?.commit;
    return typeof commit === 'string' && commit.length > 0 ? commit : null;
  } catch {
    return null;
  }
}

export function VersionRefreshToast() {
  const { t } = useLanguage();
  const [available, setAvailable] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  // Timestamp of the last check, to throttle focus/visibility bursts.
  // Stamped synchronously before the fetch, so it also blocks a re-entrant
  // call while a previous check is still in flight.
  const lastCheckedAt = useRef(0);

  const check = useCallback(async () => {
    if (Date.now() - lastCheckedAt.current < MIN_CHECK_INTERVAL_MS) return;
    lastCheckedAt.current = Date.now();
    const deployed = await fetchDeployedCommit();
    if (deployed && deployed !== BAKED_COMMIT) setAvailable(true);
  }, []);

  useEffect(() => {
    if (available) return; // update found — stop polling entirely
    // Initial check on mount, then interval + focus/visibility triggers.
    check();
    const onInterval = () => {
      // Hidden tabs skip the round-trip; the visibility handler guarantees
      // a fresh check the moment the tab is shown again.
      if (document.visibilityState === 'visible') check();
    };
    const intervalId = window.setInterval(onInterval, POLL_INTERVAL_MS);
    const onFocus = () => check();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') check();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [available, check]);

  if (!available || dismissed) return null;

  return (
    <div className="version-refresh-toast" role="status" aria-live="polite">
      <span className="version-refresh-toast__text">
        {t('version.newVersionAvailable')}
      </span>
      <Button
        type="primary"
        size="small"
        icon={<ReloadOutlined />}
        onClick={() => window.location.reload()}
      >
        {t('version.refresh')}
      </Button>
      <Button
        type="text"
        size="small"
        aria-label={t('version.dismiss')}
        icon={<CloseOutlined />}
        onClick={() => setDismissed(true)}
      />
    </div>
  );
}

export default VersionRefreshToast;
